// i18n primitives — the single source of truth for locale, the grounding-gate
// normalizer, the self-labelled-answer parser, and the locale-keyed user-story
// narrative templates. Leaf module (only `zod`) so both `config` and
// `readiness` can import it without a cycle.
//
// Three planes (i18n plan): REASONING stays English (prompts/rubrics/tool +
// field names — not here); OUTPUT PROSE is localizable (the narrative templates
// below + the web catalog); GROUNDING is byte-exact (normalizeQuote below). The
// content locale is a per-PROJECT property read from `.loom.yml` `locale` and
// pinned on the session at create — it is NOT the operator's UI locale. The UI
// locale reaches exactly one place: the language the prompt directive NAMES
// when the project sets none (`resolveSessionLocale`), never the content plane.
//
// The `locale` key says which language Loom AUTHORS in. It does NOT decide the
// narrative FRAME: that frame wraps bytes the grounding gate already greened,
// so at the default locale it follows those bytes instead (see
// `resolveContentLocale`). Nobody has ever set the key, and 10 of the 69 story
// cards Loom has framed across the two projects it writes to are German — every
// one of them was framed "As … I want … so that" around German prose.

import { z } from "zod";

import { H } from "./issue-body/index.js";

// ---------------------------------------------------------------------------
// Locale
// ---------------------------------------------------------------------------

/** Content locales Loom can author issue prose in. English is canonical for the
 *  reasoning plane; adding a locale here is the only registration needed. */
export const LOCALES = ["en", "de"] as const;
export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "en";

export const isLocale = (v: unknown): v is Locale =>
  typeof v === "string" && (LOCALES as readonly string[]).includes(v);

/** Coerce an unknown (old session row, malformed config) to a valid locale,
 *  defaulting to English — never throws. */
export const asLocale = (v: unknown): Locale => (isLocale(v) ? v : DEFAULT_LOCALE);

export const localeSchema = z.enum(LOCALES);

/** Human-readable language name, in English (the directive is read by an
 *  English-reasoning model). */
const LOCALE_NAMES: Record<Locale, string> = { en: "English", de: "German" };

/** Per-locale style note appended to the prompt directive — keeps the agent's
 *  live prose in the same register/orthography as the web UI copy.
 *
 *  WHERE IT REACHES. Every directive that NAMES German injects this same
 *  text: the pinned `locale: de` one, and the default-locale one when the
 *  session's language is German (`resolveSessionLocale`). It once reached only
 *  the first, and no project has ever set `locale:` — so a German-speaking
 *  project on the default locale got no orthography instruction at all and
 *  transliterated every diacritic ("Prioritaet", "waehrend", "verlaesst") into
 *  permanent issue bodies. It no longer rides an English directive behind an
 *  "if German" clause: that clause was the only language the English prompt
 *  named, and it turned wholly English sessions German (see
 *  `localePromptDirective`).
 *
 *  The umlaut clause is not redundant with the "ss" one. Live German output on
 *  a default-locale project transliterated every diacritic ("Prioritaet",
 *  "waehrend", "verlaesst"), and a folded issue body propagated into six
 *  sub-issues through a breakdown run — so the note states the boundary of the
 *  "ss" rule positively rather than leaving "ß → ss" as the only orthography
 *  sentence a model reading it can generalise from.
 *
 *  The carve-out at the end is load-bearing twice over, and both halves are
 *  about the SAME gate. (1) `normalizeQuote` folds whitespace, case and NFC and
 *  NOT "ae" → "ä", so a model that "corrects" a user's own transliterated bytes
 *  on the way into a grounding quote produces a quote that no longer matches
 *  the turn it came from — a false RED on a field the user already answered.
 *  (2) This note is appended LAST, after the directive's own NEVER-translate
 *  sentence, so an orthography rule stated without its exemptions reads as
 *  overriding them: the self-labelled `Label: value` citations must stay
 *  ASCII English ("Priority: High"), which is why the umlaut example here is
 *  deliberately NOT a field label. */
const LOCALE_STYLE: Partial<Record<Locale, string>> = {
  de:
    `Write SWISS High German (Schweizer Hochdeutsch): NEVER use the letter "ß" — ` +
    `always "ss" (Strasse, Grösse, schliessen, dass). That rule is about "ß" ` +
    `ALONE: write the umlauts "ä", "ö", "ü" (and "Ä", "Ö", "Ü") as those ` +
    `letters — never "ae"/"oe"/"ue" ("Qualität", not "Qualitaet"; "während", ` +
    `not "waehrend"). That governs the prose YOU author, and it does NOT ` +
    `reopen anything exempted above: the self-labelled "Label: value" answers ` +
    `you cite for grounding stay verbatim English, and the user's own words ` +
    `stay byte-for-byte as they wrote them, folded or not. Address the user ` +
    `informally as "du", not "Sie". Keep it natural and concise — not a literal ` +
    `translation of English phrasing.`,
};

export const localeName = (locale: Locale): string => LOCALE_NAMES[locale];

/**
 * The language a session's agent writes to the user in when nothing in the
 * conversation says otherwise. An explicit `.loom.yml locale` wins; else the
 * interface language the user opened the session from; else English.
 *
 * `configured` is `unknown` on purpose: it is the RAW config value, before
 * `asLocale` collapses an absent key into `en`, because "the project says
 * English" and "the project says nothing" have to answer differently here. An
 * unsupported value ("xx") counts as absent, like a malformed config anywhere
 * else in this module.
 *
 * This is the DIRECTIVE's language only. The content plane (the narrative
 * frame, the grounding strip, `## Build notes`) keeps reading the pinned
 * session locale, which at the default follows the bytes the user actually
 * wrote (`resolveContentLocale`) — so an interface language can never re-frame
 * a card written in the other language.
 */
export const resolveSessionLocale = (
  configured: unknown,
  ui: Locale | undefined,
): Locale => (isLocale(configured) ? configured : (ui ?? DEFAULT_LOCALE));

/** The two sentences every directive carries, whatever language it names. */
const VERBATIM_SENTENCE =
  `When you ground a field, quote the user's words VERBATIM; never ` +
  `translate, paraphrase, or "clean up" their wording (the readiness gate ` +
  `matches bytes, so a paraphrase fails to ground).`;
const NEVER_TRANSLATE_SENTENCE =
  `NEVER translate, whatever language you end up writing in: tool names, MCP ` +
  `arguments, code symbols, field names, the self-labelled "Label: value" ` +
  `answers you cite for grounding (e.g. "Priority: High", "Milestone: 7.1"), ` +
  `the "## Acceptance criteria" / "## Definition of Done" section headings, ` +
  `or any text inside backticks or <!-- --> markers — those stay verbatim ` +
  `English.`;

/**
 * The content-locale directive injected into BOTH the servant and sandbox
 * prompts (identical copy so the boot→sandbox handoff doesn't switch register).
 * Always returns a directive — there is no locale for which the agent is left
 * without a language instruction.
 *
 * TWO SHAPES. `locale` is the pinned session locale (`.loom.yml locale`, `en`
 * when absent). A non-default one is the PROJECT's language and is pinned
 * absolutely. At the default, the directive NAMES a language — `sessionLocale`,
 * see `resolveSessionLocale` — and lets the user override it by writing in
 * another one.
 *
 * WHY IT NAMES THE LANGUAGE instead of mirroring "the language the user writes
 * to you in". Measured offline against the real composed sandbox prompt and the
 * Focus refine seed (claude-sonnet-5, 3 samples per condition, the language of
 * the first prose line):
 *   - the mirror text with a German worked example ("If that language turns
 *     out to be German, then also: …") answered a WHOLLY English seed in German
 *     3/3 — the example was the only language named anywhere in the prompt;
 *   - the same text with the example deleted answered in English 1/3,
 *     Portuguese 1/3, Spanish 1/3: the seed is Loom's machine text, so there is
 *     no user prose to mirror and the choice is unanchored;
 *   - deleting the example did not survive a German issue title either (German
 *     3/3), and German titles are common on the projects Loom writes to;
 *   - this text, naming English, answered English 3/3 on an English title and
 *     on a German one (2/2 prose replies), and 3/3 with the Figma block on;
 *     naming German, it answered German 3/3 even on the English seed.
 * So: no language is ever named that the session is not in — an English
 * session never reads the word "German" — and the name is stated, never
 * inferred. The text below is byte-identical to the one measured; a change to
 * it is a change to the agent and wants the same experiment re-run.
 *
 * The parenthetical is exact for the case it was written for (no `.loom.yml
 * locale`, the interface language known at create). It is loose in two rarer
 * ones, accepted rather than adding untested variants: a project that sets
 * `locale: en` (none does today), and a session whose interface language is
 * unknown — an older client, or a session rebuilt after an instance recycle by
 * a WebSocket attach before any `/reconnect` could send one
 * (`AgentSession.uiLocale` is memory-only) — which falls back to English and
 * still follows a user who writes in another language.
 *
 * The never-translate clause is load-bearing: when the agent re-cites
 * "Priority: High" to ground the enum slot mid-German-sentence, it must quote
 * the ENGLISH bytes — `parseLabelled` is ASCII-and-contract-keyed and the gate
 * matches bytes, so a "helpfully" translated "Priorität: Hoch" would silently
 * fail to ground. The German style note rides every directive that names
 * German, byte-identical in both shapes, so a project that later sets
 * `locale: de` does not switch orthography rules mid-flight.
 */
export const localePromptDirective = (
  locale: Locale,
  sessionLocale?: Locale,
): string => {
  if (locale === DEFAULT_LOCALE) {
    const language = sessionLocale ?? DEFAULT_LOCALE;
    const name = LOCALE_NAMES[language];
    return [
      `LANGUAGE: ${name} (the user's interface language; the project sets none).`,
      `Author ALL user-facing prose — chat replies, questions, the issue title and`,
      `description — in ${name}, unless the user writes to you in another`,
      `language; then answer in that language and follow them if they switch.`,
      VERBATIM_SENTENCE,
      NEVER_TRANSLATE_SENTENCE,
      LOCALE_STYLE[language] ?? "",
    ]
      .filter(Boolean)
      .join(" ");
  }
  const name = LOCALE_NAMES[locale];
  return [
    `PROJECT LANGUAGE: ${name}.`,
    `Reason internally in English, but author ALL user-facing prose — chat`,
    `replies, questions, the issue title and description — in ${name}.`,
    `When you ground a field, quote the user's ${name} words VERBATIM; never`,
    `translate, paraphrase, or "clean up" their wording (the readiness gate`,
    `matches bytes, so a paraphrase fails to ground).`,
    `NEVER translate, even mid-${name}-sentence: tool names, MCP arguments, code`,
    `symbols, field names, the self-labelled "Label: value" answers you cite for`,
    `grounding (e.g. "Priority: High", "Milestone: 7.1"), the "## Acceptance`,
    `criteria" / "## Definition of Done" section headings, or any text inside`,
    `backticks or <!-- --> markers — those stay verbatim English.`,
    LOCALE_STYLE[locale] ?? "",
  ]
    .filter(Boolean)
    .join(" ");
};

// ---------------------------------------------------------------------------
// Grounding-gate normalization (the byte-exact plane).
//
// The gate is pure normalized-substring containment with NO word boundaries
// (propose-card.ts findQuoteTurn). Adding `.normalize("NFC")` collapses the
// precomposed-vs-decomposed umlaut mismatch (macOS/IME/PDF paste emit NFD) that
// otherwise false-REDs a genuine German quote. It is symmetric (both turn and
// quote pass through it) and a no-op for ASCII English. We deliberately do NOT
// expand ß→ss: that would fix ß-casing but collapse distinct words (Maße/Masse,
// Buße/Busse) into a false-GREEN under the no-word-boundary matcher — a silent
// integrity hole strictly worse than a recoverable false-RED.
// ---------------------------------------------------------------------------

/** Normalize a quote/turn for substring containment: collapse whitespace, trim,
 *  lowercase, NFC. The sole comparison primitive for user-evidence grounding. */
export const normalizeQuote = (s: string): string =>
  s.replace(/\s+/gu, " ").trim().toLowerCase().normalize("NFC");

/**
 * Parse a self-labelled answer span ("Milestone: Sprint 12", "Priority: High")
 * into `{ label, value }`, or null for free text. The label class is `\p{L}`
 * (any Unicode letter) so an accented label parses at all; the milestone/
 * priority widgets keep an English label token by contract, but free-typed
 * localized labels no longer silently fail to parse.
 */
export const parseLabelled = (
  text: string,
): { label: string; value: string } | null => {
  const m = /^\s*(\p{L}[\p{L} _-]{0,40}?)\s*:\s*(\S.*)$/u.exec(text);
  if (!m) return null;
  return { label: m[1]!.trim().toLowerCase(), value: m[2]!.trim() };
};

// ---------------------------------------------------------------------------
// User-story narrative templates (the output-prose plane).
//
// renderNarrative composes "As {role}, I want {means}, so that {ends}." from the
// green field values. The connectives are hardcoded English today, so a German
// session produces "As a Als Nutzer, I want möchte ich …" — the inbound clause
// also fails to strip the German connective and DOUBLES. Each locale supplies
// its own strip + compose. Strip recognizes BOTH English and the target-language
// connectives (the agent is English-tuned and may emit either), so a clause is
// never doubled regardless of which language the value arrived in.
//
// Section HEADINGS are body scaffolding owned by `@loom/shared/issue-body`,
// not by this module. Three person-written sections (`## What you need`,
// `## Business outcome`, `## Acceptance criteria`) have a German spelling
// (`H_DE`) a writer uses for a new section of a German ticket; the parser
// reads both spellings as one section. `## Definition of Done` and the marked
// blocks stay English in every locale. The narrative here is prose only.
// ---------------------------------------------------------------------------

export interface NarrativeTemplate {
  /**
   * Strip a leading role connective ("As a" / "Als"), returning the clause as
   * it should read after the template's own connective is prepended.
   *
   * `foreignContent` is the CARD-level language signal (`narrativeContentIsForeign`
   * over every clause, not just this one). The English template INVENTS an
   * indefinite article for a bare role, and a one-word role is far too small a
   * sample to tell "Kundschaft" from "Customer" — live #1078 was written "As a
   * Kundschaft, …" from a session that was German in every other clause. The
   * caller knows the whole card; the clause does not.
   */
  readonly stripRole: (
    v: string,
    opts?: { readonly foreignContent?: boolean },
  ) => string;
  readonly stripMeans: (v: string) => string;
  readonly stripEnds: (v: string) => string;
  /** Compose the one-line user story from the stripped clauses. */
  readonly compose: (role: string, means: string, ends: string) => string;
  /**
   * The same sentence for an `ends` value that already carries a connective
   * this template's own cannot stand in front of — "to reduce the support
   * load", "um … zu …". `spliceNarrativeClause` decides which of the two
   * applies (`ClauseSplice.connective`); stacking them produced "so that to
   * reduce the support load" in the written issue while the review screen,
   * which already honours the flag, showed the correct sentence.
   */
  readonly composeOwnEnds: (role: string, means: string, ends: string) => string;
  /**
   * THE FRAME CHOICE, of which the two functions above are the two commonest
   * cases. Given the SHAPE each clause arrived in, pick a sentence that can
   * carry it — rather than splicing a clause the frame's connective cannot
   * stand in front of.
   *
   * The values are never touched: `spliceNarrativeClause` "never touches a
   * word" and this changes nothing about that. What moves is the template's own
   * function words, which is the only half Loom is entitled to author.
   *
   * Why the frame has to bend at all: the merge PROMOTES a user's own later
   * words past the `means` mint bound (`supersedingMintRefusal`) — silently
   * losing their correction is worse than an unusual sentence — so a `means`
   * carrying its own subject and finite verb can and does reach this composer.
   * Spliced after "I want" it wrote, to a real issue: "As a Kundschaft, I want
   * Die Kundschaft findet ihre Lieferadresse … vor und kann sie überschreiben,
   * so that …" (live #1078). With the frame bent it reads "Als Kundschaft: Die
   * Kundschaft findet … vor und kann sie überschreiben, damit …" — the same
   * bytes, in a sentence a person could have written.
   *
   * `carriesOwnSubject` (@loom/shared/readiness) is the shape test both callers
   * share, and it is precision-biased: an ordinary fragment never reaches these
   * branches, so the well-formed story stays byte-identical.
   */
  readonly composeStory: (
    parts: {
      readonly role: string;
      readonly means: string;
      readonly ends: string;
    },
    shape: {
      /** The role is a sentence ("Ich bin die Kundschaft, die oft bestellt"),
       *  so it stands on its own instead of following "As …". */
      readonly roleCarriesSubject?: boolean;
      /** The means is a sentence, so the frame introduces it instead of
       *  completing "I want …" with it. */
      readonly meansCarriesSubject?: boolean;
      /** False when the ends brings its own connective — see `composeOwnEnds`.
       *  Defaults to true. */
      readonly endsConnective?: boolean;
    },
  ) => string;
  /**
   * Strip a leading user-story connective from a clause value WITHOUT re-adding
   * one (unlike `stripRole`, which prepends "a "). Used by the F1 value-grounding
   * check: a card value the model wrapped as "As a planner" must still ground
   * against a user turn that just said "planner". Returns the clause with the
   * connective removed, or the input unchanged when there is none.
   */
  readonly stripGroundingConnective: (
    clause: "role" | "means" | "ends",
    v: string,
  ) => string;
  /**
   * Residual duplicate connectives the `strip*` regexes above DON'T catch, fed
   * to `normalizeNarrativeClause` before compose re-adds the canonical one. The
   * ends-strip only removes the full "so that " — a value that opens with a bare
   * "So …" (the model's own words after the F1 verbatim-value fix) would double
   * into "so that So …", so the bare "so " is listed here. Omit → nothing extra
   * stripped. The helper tries the LONGEST match first, so listing both
   * "so that " and "so " is order-safe.
   */
  readonly meansConnectives?: readonly string[];
  readonly endsConnectives?: readonly string[];
  /**
   * Orthography knobs for `normalizeNarrativeClause`, both OFF unless set. EN
   * turns them on: a model/user-authored clause value spliced mid-sentence must
   * read as a continuation — "I want View a list" → "I want view a list"
   * (lowercaseClauseLead) — and the role's indefinite article must agree with
   * the noun — "As a Internal operator" → "As an Internal operator"
   * (reconcileArticle). DE keeps both OFF: German capitalizes nouns
   * mid-sentence and has no a/an agreement.
   */
  readonly lowercaseClauseLead?: boolean;
  readonly reconcileArticle?: boolean;
  /**
   * The infinitive marker this template's frame supplies when a clause opens on
   * a BARE VERB — "to" in English, nothing in German.
   *
   * The frame is "I want {means}" and "so that {ends}", and both take a
   * COMPLEMENT, not a command. Loom's own drafts are written as imperative
   * headlines ("Add an audit trail …", "Trace who changed what …"), the user
   * accepts them with one click, and the sentence written to GitLab was "I want
   * add an audit trail …, so that trace who changed what …" (live #1081, from
   * the drafts alone — the DEFAULT path, not an edge case). The marker is a
   * function word the TEMPLATE contributes, exactly like the connective it sits
   * next to, so supplying it repairs the join without touching a byte of the
   * value the grounding gate greened. `spliceNarrativeClause` decides when it
   * applies.
   *
   * German gets none on purpose: a German infinitive is verb-final ("einen
   * Prüfpfad hinzufügen"), so no marker prepended to an imperative can fix it —
   * that shape has to be refused or re-asked, not patched.
   */
  readonly infinitiveMarker?: string;
}

/**
 * The English frame, for each shape a clause can arrive in. The first branch is
 * the ordinary story and is byte-identical to what it has always been.
 *
 * The COLON is the whole repair for a sentence-shaped means: "As a customer:
 * The customer finds their delivery address prefilled at checkout, so that they
 * type less." A comma there would read as an apposition to the role, and "I
 * want" in front of it is the ungrammatical splice this exists to remove.
 */
const enStory: NarrativeTemplate["composeStory"] = (
  { role, means, ends },
  shape,
) => {
  const tail = shape.endsConnective === false ? ends : `so that ${ends}`;
  if (shape.roleCarriesSubject === true) {
    const want = shape.meansCarriesSubject === true ? means : `I want ${means}`;
    return `${role}. ${want}, ${tail}.`;
  }
  return shape.meansCarriesSubject === true
    ? `As ${role}: ${means}, ${tail}.`
    : `As ${role}, I want ${means}, ${tail}.`;
};

/** The German frame. Same three shapes; "möchte ich" becomes "Ich möchte" when
 *  the role has taken the sentence opening for itself. */
const deStory: NarrativeTemplate["composeStory"] = (
  { role, means, ends },
  shape,
) => {
  const tail = shape.endsConnective === false ? ends : `damit ${ends}`;
  if (shape.roleCarriesSubject === true) {
    const want =
      shape.meansCarriesSubject === true ? means : `Ich möchte ${means}`;
    return `${role}. ${want}, ${tail}.`;
  }
  return shape.meansCarriesSubject === true
    ? `Als ${role}: ${means}, ${tail}.`
    : `Als ${role} möchte ich ${means}, ${tail}.`;
};

// English — byte-identical to the legacy inline renderNarrative clauses.
const EN: NarrativeTemplate = {
  stripRole: (v, opts) => {
    const m = /^as\s+(an?\s+.*)$/is.exec(v); // "As a user" → "a user"
    if (m) return m[1]!.trim();
    if (/^an?\s/i.test(v)) return v; // "a user" / "an admin" → keep
    // Inventing an article is English-only. A German plural takes none, so
    // prepending one wrote "As an Interne Mitarbeitende der Organisation …".
    // `foreignContent` is the same judgement made over the WHOLE card, which is
    // what catches a one-word German role no per-clause test can see.
    if (opts?.foreignContent === true || looksNonEnglish(v)) return v;
    // An English PLURAL takes no indefinite article either: "As an Admins /
    // superusers of the admin area" (live #1081) is the same defect in the
    // template's own language. No article is always grammatical in front of a
    // plural ("As Admins …"), so this only ever removes a wrong word.
    if (headNounIsPlural(v)) return v;
    // Nor does a role that already opens on a determiner or a quantifier:
    // "As everyone who uses the dashboard", "As the finance team". Live #98 was
    // written "As an everyone who uses the dashboard" — the article invented
    // here, then agreed to "an" by `reconcileLeadingArticle`.
    if (opensWithDeterminer(v)) return v;
    return v ? `a ${v}` : v; // "user" → "a user"
  },
  stripMeans: (v) =>
    v.replace(/^i\s+(?:want|need|wish|would\s+like|'d\s+like)\s+/i, "").trim(),
  stripEnds: (v) => v.replace(/^so\s+that\s+/i, "").trim(),
  compose: (role, means, ends) => enStory({ role, means, ends }, {}),
  composeOwnEnds: (role, means, ends) =>
    enStory({ role, means, ends }, { endsConnective: false }),
  composeStory: enStory,
  infinitiveMarker: "to",
  stripGroundingConnective: (clause, v) => {
    // "als" as well as "as": the DE template already strips BOTH locales'
    // connectives and this one only knew its own, so a model that wrapped the
    // user's bare "Nutzer:in der Screening-App" as "Als Nutzer:in …" failed to
    // ground on an unconfigured (= `en`) project — which is every project.
    // Stripping only ever widens what matches a turn the user actually wrote.
    if (clause === "role") {
      return v.replace(/^(?:als\s+|as\s+)?(?:an?\s+)?/i, "").trim();
    }
    if (clause === "means") {
      return v
        .replace(/^i\s+(?:want|need|wish|would\s+like|'d\s+like)\s+(?:to\s+)?/i, "")
        .trim();
    }
    return v.replace(/^so\s+that\s+/i, "").trim();
  },
  endsConnectives: ["so that ", "so "],
  lowercaseClauseLead: true,
  reconcileArticle: true,
};

// German — "Als {role} möchte ich {means}, damit {ends}." Strips German AND
// English connectives so a value authored in either language renders cleanly.
const DE: NarrativeTemplate = {
  // The article strip does NOT require a connective in front of it. EN's has
  // always been optional; DE's was not, which never mattered while this
  // template was reachable only on a `locale: de` project — i.e. never. It is
  // reachable now via `resolveContentLocale`, and the shape that reaches it is
  // exactly a mixed card: an English role under German means/ends renders
  // "Als a Product Owner möchte ich …" unless the bare "a" comes off here.
  // Render-only — grounding goes through `stripGroundingConnective` below.
  stripRole: (v) => v.replace(/^(?:(?:als|as)\s+)?(?:an?\s+)?/i, "").trim(),
  // "will" is the one German verb here that is ALSO an ordinary English
  // auxiliary, so it only strips as part of "ich will" / "will ich". Bare
  // "will …" reaches this template whenever a card's role and criteria are
  // German but its `means` is English (live #1096 is that shape), and
  // `stripMeans` deleting a word from a value the grounding gate greened
  // byte-exact is the one thing the whole foreign-content asymmetry exists to
  // prevent: DE.stripMeans("will be able to export the component list")
  // returned "be able to export the component list".
  stripMeans: (v) =>
    v
      .replace(
        /^(?:ich\s+)?(?:möchte|moechte|wünsche|wuensche|brauche|benötige|benoetige)\s+(?:ich\s+)?/iu,
        "",
      )
      .replace(/^(?:ich\s+will|will\s+ich)\s+/iu, "")
      .replace(/^i\s+(?:want|need|wish|would\s+like|'d\s+like)\s+(?:to\s+)?/i, "")
      .trim(),
  stripEnds: (v) => v.replace(/^(?:damit|so\s+that)\s+/i, "").trim(),
  compose: (role, means, ends) => deStory({ role, means, ends }, {}),
  composeOwnEnds: (role, means, ends) =>
    deStory({ role, means, ends }, { endsConnective: false }),
  composeStory: deStory,
  stripGroundingConnective: (clause, v) => {
    if (clause === "role") return v.replace(/^(?:als|as)\s+(?:an?\s+)?/i, "").trim();
    if (clause === "means") {
      // Same "will" bound as `stripMeans` above — and here a wrong strip
      // widens what grounds, not just what renders.
      return v
        .replace(
          /^(?:ich\s+)?(?:möchte|moechte|wünsche|wuensche|brauche|benötige|benoetige)\s+(?:ich\s+)?(?:zu\s+)?/iu,
          "",
        )
        .replace(/^(?:ich\s+will|will\s+ich)\s+(?:zu\s+)?/iu, "")
        .replace(/^i\s+(?:want|need|wish|would\s+like|'d\s+like)\s+(?:to\s+)?/i, "")
        .trim();
    }
    return v.replace(/^(?:damit|so\s+that)\s+/i, "").trim();
  },
  // Bare "so …" / English "so that …" residuals only; German nouns stay
  // capitalized mid-sentence and there is no a/an agreement, so the two
  // orthography knobs stay off.
  endsConnectives: ["so that ", "so "],
};

export const NARRATIVE_TEMPLATES: Record<Locale, NarrativeTemplate> = {
  en: EN,
  de: DE,
};

/** Two structural section headings, in their English spelling. `## Acceptance
 *  criteria` also has a German one (`H_DE.acceptanceCriteria`) that the
 *  body writer uses for a new section of a German ticket and the parser reads
 *  as the same section; `## Definition of Done` is the same in both.
 *
 *  The spelling lives in `@loom/shared/issue-body`'s `H`, which owns every
 *  heading a body is made of; this is the prose plane's view of two of them and
 *  keeps its name so call sites do not move. A leaf-module import: `issue-body`
 *  imports nothing from here. */
export const SECTION_HEADINGS = {
  acceptanceCriteria: H.acceptanceCriteria,
  definitionOfDone: H.definitionOfDone,
} as const;

// ---------------------------------------------------------------------------
// `## Build notes` copy — the block the breakdown apply composes into every
// sub-issue body.
//
// PROSE plane: the bold labels and the sentences around them are localized. The
// `## Build notes` heading and the two HTML markers are NOT — they are parse
// plane, frozen English, exactly the split `SECTION_HEADINGS` makes above.
// Nothing here localizes a closed vocabulary either: the `AC<n>` ids, the
// `create` / `modify` verbs and the LoC buckets stay verbatim, because every
// one of them is also a key some other layer matches on.
//
// Swiss High German throughout: never "ß".
// ---------------------------------------------------------------------------

export interface BuildNotesLabels {
  readonly closes: string;
  readonly notInThisPiece: string;
  readonly coordinate: string;
  readonly howToBuild: string;
  readonly whereItLands: string;
  readonly effort: string;
  readonly definitionOfDone: string;
  readonly outOfScope: string;
  readonly whySplit: string;
}

export const BUILD_NOTES_LABELS: Record<Locale, BuildNotesLabels> = {
  en: {
    closes: "What this piece closes",
    notInThisPiece: "Not in this piece",
    coordinate: "Coordinate with other pieces",
    howToBuild: "How to build it",
    whereItLands: "Where the change lands",
    effort: "Effort",
    definitionOfDone: "Definition of done",
    outOfScope: "Out of scope for the whole ticket",
    whySplit: "Why the work was split this way",
  },
  de: {
    closes: "Was dieses Teilstück abschliesst",
    notInThisPiece: "Nicht in diesem Teilstück",
    coordinate: "Mit anderen Teilstücken abstimmen",
    howToBuild: "So wird es gebaut",
    whereItLands: "Wo die Änderung landet",
    effort: "Aufwand",
    // Deliberately NOT translated: "Definition of done" is the term of art the
    // rest of this product (and the `dod_criteria` field label) already uses in
    // both locales.
    definitionOfDone: "Definition of done",
    outOfScope: "Ausserhalb des Umfangs des ganzen Tickets",
    whySplit: "Warum die Arbeit so aufgeteilt wurde",
  },
};

export interface BuildNotesSentences {
  /** "AC2 and AC5 of the 5 acceptance criteria on #42." */
  readonly closes: (ids: string, count: number, parentIid: number) => string;
  /** Lead-in after the "Not in this piece" label. */
  readonly notInThisPiece: (parentIid: number) => string;
  /** Caveat for the per-criterion file map (tier 1). */
  readonly whereItLandsSized: string;
  /** Caveat for the flat persisted file list (tier 2). */
  readonly whereItLandsFiles: string;
  /** "4 files, M." */
  readonly effort: (filesTouched: number, locBucket: string) => string;
  /** The closing pointer at the parent's `## Implementation details`. */
  readonly planPointer: (parentIid: number) => string;
  /** Lead-in after the "Out of scope for the whole ticket" label, on a CHILD. */
  readonly outOfScope: (parentIid: number) => string;
  /**
   * The same label's lead-in on the PARENT itself.
   *
   * The child sentence names the parent by iid ("taken off #42"), which reads as
   * a pointer somewhere else — wrong on the very issue it is talking about. The
   * parent is also the one place where the rows are the WHOLE story: it is the
   * body a reader opens to learn the epic's scope, and its `## Acceptance
   * criteria` no longer carries them (the park removed the row from the verified
   * green before apply reconstructed the description), so nothing else on the
   * page says the work was considered and dropped rather than forgotten.
   */
  readonly outOfScopeParent: string;
  /**
   * Lead-in after the "Definition of done" label. R2-R: the block is the
   * PARENT's DoD verbatim (there is no per-criterion structure to scope it
   * from), so every child of a multi-piece split renders the same bar — live,
   * a child whose whole job was a filename and a button label carried a DoD
   * asserting a sibling's export-button work as its own. Labelling it as the
   * parent's bar for the whole story, not this piece's own, matches the
   * honesty `howToBuildRetired` already applies to a stale build note.
   */
  readonly definitionOfDoneScope: (parentIid: number) => string;
  /** "AC5 is also covered by piece 3, "…"." */
  readonly alsoCoveredBy: (
    criterionId: string,
    ordinal: number,
    title: string,
  ) => string;
  /** "Piece 1, "…", touches 3 of the same files." — COUNTS, never paths. */
  readonly sharesFiles: (
    ordinal: number,
    title: string,
    count: number,
  ) => string;
  /** Joins a short list of `AC<n>` ids: "AC1, AC2 and AC5". */
  readonly joinIds: (ids: ReadonlyArray<string>) => string;
  /**
   * Replaces Loom's split rationale once the user has re-shaped the partition
   * by hand. Every fact in it is DERIVED at apply time from the pieces actually
   * being created, so unlike the prose it displaces it cannot contradict them.
   */
  readonly whySplitAdjusted: (
    pieces: number,
    criteriaCount: number,
    parentIid: number,
  ) => string;
  /**
   * Stands in for a build note the reducer retired. An unexplained missing
   * section reads as a bug next to siblings that have one; this says which of
   * the two possible reasons it is.
   */
  readonly howToBuildRetired: string;
}

const joinWith =
  (conjunction: string) =>
  (ids: ReadonlyArray<string>): string => {
    if (ids.length === 0) return "";
    if (ids.length === 1) return ids[0]!;
    return `${ids.slice(0, -1).join(", ")} ${conjunction} ${ids[ids.length - 1]!}`;
  };

/**
 * Count-agreement for OUTPUT PROSE composed server-side. `apps/web/src/i18n/
 * messages/plurals.ts` is the CLDR-style catalog the round-1 "1 Punkte" fix
 * (cf89372) routed through — it exists only for the browser, which is not in
 * this file's process (`BUILD_NOTES_SENTENCES` composes a GitLab issue body on
 * the server, and packages/shared must not depend on apps/web). This is the
 * server-side twin: same discipline (a declared `{one, other}` pair per call
 * site, never a fourth hand-rolled `n === 1 ? … : …` inline), scoped to the
 * two-form English/German split this module's sentences actually need.
 */
export const pluralOf = (
  count: number,
  forms: { readonly one: string; readonly other: string },
): string => (count === 1 ? forms.one : forms.other);

export const BUILD_NOTES_SENTENCES: Record<Locale, BuildNotesSentences> = {
  en: {
    closes: (ids, count, parentIid) =>
      `${ids} of the ${String(count)} acceptance criteria on #${String(parentIid)}.`,
    notInThisPiece: (parentIid) =>
      `other pieces of #${String(parentIid)} deliver these; do not build them here:`,
    whereItLandsSized:
      "estimated during refinement from a read of the repository, never " +
      "re-checked against your branch. A starting point, not a specification.",
    whereItLandsFiles:
      "candidate files recorded when this piece was sized, never re-checked " +
      "against your branch. A starting point, not a specification.",
    effort: (filesTouched, locBucket) =>
      `${String(filesTouched)} ${pluralOf(filesTouched, { one: "file", other: "files" })}, ${locBucket}.`,
    planPointer: (parentIid) =>
      `The full technical plan for #${String(parentIid)} is on the parent issue ` +
      "under `## Implementation details`. Read it for context; only the parts " +
      "touching the files above belong to this piece.",
    outOfScope: (parentIid) =>
      `the requester took these off #${String(parentIid)} when it was split; ` +
      "do not build them:",
    outOfScopeParent:
      "these were taken off this ticket when it was split; no sub-issue " +
      "delivers them:",
    definitionOfDoneScope: (parentIid) =>
      `the bar for the whole story on #${String(parentIid)}, not this piece ` +
      "alone — see \"What this piece closes\" above for what this piece is " +
      "actually responsible for:",
    alsoCoveredBy: (criterionId, ordinal, title) =>
      `${criterionId} is also covered by piece ${String(ordinal)}, "${title}".`,
    sharesFiles: (ordinal, title, count) =>
      `Piece ${String(ordinal)}, "${title}", touches ${String(count)} of the same files.`,
    joinIds: joinWith("and"),
    whySplitAdjusted: (pieces, criteriaCount, parentIid) =>
      `the requester re-shaped this split by hand before the pieces were ` +
      `created: ${String(pieces)} pieces covering the ${String(criteriaCount)} ` +
      `acceptance criteria on #${String(parentIid)}. Loom's own reasoning ` +
      `described the split it first proposed, not this one, so it is not ` +
      `repeated here.`,
    howToBuildRetired:
      "Loom wrote a note for this piece, then the split changed and the note no " +
      "longer described the work above — so it was dropped rather than left to " +
      "mislead. Plan this piece from its acceptance criteria.",
  },
  de: {
    closes: (ids, count, parentIid) =>
      `${ids} von den ${String(count)} Akzeptanzkriterien auf #${String(parentIid)}.`,
    notInThisPiece: (parentIid) =>
      `andere Teilstücke von #${String(parentIid)} liefern diese; baue sie hier nicht:`,
    whereItLandsSized:
      "bei der Verfeinerung aus einem Blick ins Repository geschätzt und nie " +
      "gegen deinen Branch geprüft. Ein Ausgangspunkt, keine Spezifikation.",
    whereItLandsFiles:
      "beim Schätzen dieses Teilstücks festgehalten und nie gegen deinen " +
      "Branch geprüft. Ein Ausgangspunkt, keine Spezifikation.",
    effort: (filesTouched, locBucket) =>
      `${String(filesTouched)} ${pluralOf(filesTouched, { one: "Datei", other: "Dateien" })}, ${locBucket}.`,
    planPointer: (parentIid) =>
      `Der vollständige technische Plan für #${String(parentIid)} steht auf dem ` +
      "übergeordneten Ticket unter `## Implementation details`. Lies ihn als " +
      "Kontext; nur die Teile, die die Dateien oben betreffen, gehören zu " +
      "diesem Teilstück.",
    outOfScope: (parentIid) =>
      `die anfragende Person hat diese beim Aufteilen von #${String(parentIid)} ` +
      "gestrichen; baue sie nicht:",
    outOfScopeParent:
      "diese wurden beim Aufteilen von diesem Ticket gestrichen; kein " +
      "Teilstück liefert sie:",
    definitionOfDoneScope: (parentIid) =>
      `der Massstab für die ganze Story auf #${String(parentIid)}, nicht nur ` +
      "für dieses Teilstück — was dieses Teilstück tatsächlich leistet, steht " +
      "oben unter \"Was dieses Teilstück abschliesst\":",
    alsoCoveredBy: (criterionId, ordinal, title) =>
      `${criterionId} wird auch von Teilstück ${String(ordinal)}, "${title}", abgedeckt.`,
    sharesFiles: (ordinal, title, count) =>
      `Teilstück ${String(ordinal)}, "${title}", berührt ${String(count)} derselben Dateien.`,
    joinIds: joinWith("und"),
    whySplitAdjusted: (pieces, criteriaCount, parentIid) =>
      `die anfragende Person hat diese Aufteilung von Hand angepasst, bevor die ` +
      `Teilstücke erstellt wurden: ${String(pieces)} Teilstücke, welche die ` +
      `${String(criteriaCount)} Akzeptanzkriterien auf #${String(parentIid)} ` +
      `abdecken. Die Begründung von Loom beschrieb die zuerst vorgeschlagene ` +
      `Aufteilung und damit nicht diese — sie wird hier deshalb nicht wiederholt.`,
    howToBuildRetired:
      "Loom hatte eine Notiz für dieses Teilstück, dann hat sich die Aufteilung " +
      "geändert und die Notiz beschrieb die Arbeit oben nicht mehr — sie wurde " +
      "deshalb verworfen und nicht irreführend stehen gelassen. Plane dieses " +
      "Teilstück anhand seiner Akzeptanzkriterien.",
  },
};

// ---------------------------------------------------------------------------
// `## Design` copy — the block the refine apply carries onto the ticket for
// every Figma link the requester pasted while refining it.
//
// Same split as `## Build notes` above: PROSE plane. The `## Design` heading and
// both HTML markers are parse plane — frozen English in every locale — and only
// the lead-in and the stamp labels are localized.
//
// What the stamp is allowed to say is the whole point, so it is fixed here and
// nowhere else: Figma's `version` for the file, the day it last changed in
// Figma, and the day Loom read it. No layer, component, style, frame or folder
// name; no digest; no sketch. The DATES are UTC days (`YYYY-MM-DD`) in both
// locales on purpose — a day is what the approved carry list asks for, and a
// ticket is read by whoever opens it, in whatever locale. The VERSION is Figma's
// own digits, verbatim.
//
// The lead-in addresses nobody: the ticket's reader is not necessarily the
// requester, so it names "die anfragende Person" exactly as the build-notes
// sentences already do, rather than the du-register those use where they speak
// to the person building the piece.
//
// Swiss High German throughout: never "ß".
// ---------------------------------------------------------------------------

export interface DesignLabels {
  /** Prefixes Figma's own version digits. */
  readonly version: string;
  /** Stands in when the read reported no version. */
  readonly versionUnknown: string;
  /** Prefixes the day the FILE last changed in Figma. */
  readonly lastChanged: string;
  /** Stands in when the read reported no last-change instant. */
  readonly lastChangedUnknown: string;
  /** Prefixes the day Loom read the design. */
  readonly readBy: string;
}

export const DESIGN_LABELS: Record<Locale, DesignLabels> = {
  en: {
    version: "Figma version",
    versionUnknown: "Figma version not known",
    lastChanged: "last changed in Figma",
    lastChangedUnknown: "last change not known",
    readBy: "read by Loom",
  },
  de: {
    version: "Figma-Version",
    versionUnknown: "Figma-Version nicht bekannt",
    lastChanged: "zuletzt in Figma geändert",
    lastChangedUnknown: "letzte Änderung nicht bekannt",
    readBy: "von Loom gelesen",
  },
};

export interface DesignSentences {
  /** The block's lead-in, agreeing with the number of links. */
  readonly leadIn: (count: number) => string;
  /**
   * What happens to what a person types INTO the block.
   *
   * `parseDesignSectionRows` keeps a link's own line and anything indented
   * under it, and discards every other line between the markers — which is what
   * heals a doubled hand-paste, and is also what silently deleted a requester's
   * note when they typed it as a loose paragraph in the GitLab editor. The
   * block renders as ordinary markdown in an issue body, so nothing about it
   * looks read-only. The block says the rule instead of the person discovering
   * it after the fact.
   */
  readonly editNote: string;
  /** The whole stamp for a link Loom never opened — refused, declined, not
   *  connected, or simply never asked for. The link is still written: the
   *  decline was about reading the design, not about the ticket naming it. */
  readonly notRead: string;
}

export const DESIGN_SENTENCES: Record<Locale, DesignSentences> = {
  en: {
    leadIn: (count) =>
      `The Figma ${pluralOf(count, { one: "link", other: "links" })} from ` +
      `refinement, as the requester pasted ` +
      `${pluralOf(count, { one: "it", other: "them" })}. The stamps say where ` +
      `${pluralOf(count, { one: "the design", other: "each design" })} stood ` +
      "when Loom read it. A newer version in Figma means the design has moved " +
      "since.",
    editNote:
      "A note of your own belongs on a link's line, or indented under it. Loom " +
      "rewrites this block the next time the ticket is refined, and loose text " +
      "in here is lost.",
    notRead: "Loom did not read this design.",
  },
  de: {
    leadIn: (count) =>
      `${pluralOf(count, { one: "Der Figma-Link", other: "Die Figma-Links" })} ` +
      "aus der Verfeinerung, so wie die anfragende Person " +
      `${pluralOf(count, { one: "ihn", other: "sie" })} eingefügt hat. Die ` +
      `Stempel sagen, wo ${pluralOf(count, { one: "das Design", other: "jedes Design" })} ` +
      "stand, als Loom es gelesen hat. Eine neuere Version in Figma heisst: " +
      "Das Design hat sich seither verändert.",
    editNote:
      "Eine eigene Notiz gehört auf die Zeile des Links oder eingerückt " +
      "darunter. Loom schreibt diesen Block beim nächsten Verfeinern neu, " +
      "loser Text hier drin geht verloren.",
    notRead: "Loom hat dieses Design nicht gelesen.",
  },
};

// ---------------------------------------------------------------------------
// Narrative-clause normalizer (the connective-doubling / mis-capitalization fix).
//
// renderNarrative splices a model- or user-authored clause VALUE into a fixed
// template ("As {role}, I want {means}, so that {ends}."). Once the readiness
// value is the user's own verbatim words, that value can arrive as a standalone
// sentence — "View a list …", "So support can tell …" — which, spliced raw,
// reads "I want View …" (capital mid-sentence) and "so that So support …"
// (doubled connective). This ONE pure helper repairs a clause AFTER the locale
// template's `strip*` pass and BEFORE `compose` re-adds the canonical connective.
// It never invents words — only strips a duplicate connective, lowercases a
// sentence-case lead, and reconciles the indefinite article.
// ---------------------------------------------------------------------------

export interface NarrativeClauseOpts {
  /** Leading duplicate connectives to strip (case-insensitive). At most ONE is
   *  removed; the LONGEST match wins, so ["so that ", "so "] strips "so that X"
   *  as a whole and never leaves a dangling "that X". */
  readonly connectives?: readonly string[];
  /** Lowercase a sentence-case leading character ("View" → "view") so the clause
   *  reads as a continuation. A leading acronym ("API"), a CamelCase / proper-
   *  noun token with an internal capital ("GitLab"), or the bare pronoun "I" is
   *  left intact. */
  readonly lowercaseLead?: boolean;
  /** Reconcile a leading English indefinite article (a ↔ an) to agree with the
   *  following word by a vowel-LETTER heuristic with a small consonant-/vowel-
   *  SOUND exception set. No-op when the clause carries no leading a/an. */
  readonly article?: boolean;
}

// Vowel-LETTER words that take "a" (a consonant "y"/"w" sound): the "yoo"-words
// (use/user/usage/usable/usual/unit/unique/uniform/…), euro/EU, one/once,
// ubiquitous, ukulele. Curated because a blunt `u[consonant]` rule mis-fires on
// "umbrella"/"under"/"ugly" (which correctly take "an").
const A_SOUND_VOWEL_LETTER = /^(?:uni|use|usa|usu|usab|usag|ubiq|ukulele|eu|ewe|one|once)/i;
// Consonant-LETTER words that take "an" (a silent "h"): hour/honest/honor/heir.
const AN_SOUND_CONSONANT_LETTER = /^(?:hour|honest|honou?r|honorar|heir)/i;
// An all-caps initialism (UI, API, URL, XML, SLA) is read letter-by-letter, so
// the article follows the FIRST LETTER's spoken NAME, not its written vowelness:
// "a UI"/"a URL"/"a USB" (you-…), "an API"/"an XML"/"an SLA" (ay-/ex-/ess-…).
const INITIALISM = /^[A-Z][A-Z0-9]+$/;
// Letters whose spoken name starts with a vowel sound → "an".
const AN_SOUND_LETTER = /^[AEFHILMNORSX]/;

const indefiniteArticle = (word: string): "a" | "an" => {
  if (INITIALISM.test(word)) return AN_SOUND_LETTER.test(word) ? "an" : "a";
  if (AN_SOUND_CONSONANT_LETTER.test(word)) return "an";
  if (A_SOUND_VOWEL_LETTER.test(word)) return "a";
  return /^[aeiou]/i.test(word) ? "an" : "a";
};

// After a bare leading "so", these mark it as the ADVERB "so" (= as / such /
// very), NOT the conjunction "so" (= "so that") — stripping it would drop
// meaning ("so schnell wie möglich" → "schnell wie möglich"; "so much time is
// saved" → "much time is saved"). Guards the bare-"so" strip in both locales.
const SO_IS_ADVERB =
  /^(?:much|many|far|few|little|long|fast|soon|well|very|that\b|as\b|schnell|viele?|weit|wenig|lange?|gut|bald|sehr)/i;

const stripLeadingConnective = (
  s: string,
  connectives: readonly string[],
): string => {
  const lower = s.toLowerCase();
  // Longest first so "so that " is tried before "so ".
  const ordered = [...connectives].sort((a, b) => b.length - a.length);
  for (const c of ordered) {
    if (c.length === 0 || !lower.startsWith(c.toLowerCase())) continue;
    const rest = s.slice(c.length).trimStart();
    // A bare "so" is only the "so that" conjunction here when what follows is a
    // clause, not a degree adverb — otherwise keep it (it carries meaning).
    if (c.trim().toLowerCase() === "so" && SO_IS_ADVERB.test(rest)) return s;
    return rest;
  }
  return s;
};

// Letters no English word carries, and German function words that are not also
// English words ("die", "man", "hat", "war" are deliberately absent — each is a
// perfectly ordinary English word and would false-positive an English clause).
//
// The list carries BOTH spellings of every word that has an umlaut ("fuer" and
// "für"). It used to hold only the folded half, which was harmless while it fed
// `looksNonEnglish` / `narrativeContentIsForeign` — both test
// NON_ENGLISH_LETTER first, so "für" already counted. It stopped being harmless
// once `resolveContentLocale` began counting DISTINCT members of this set:
// correct German orthography scored zero there, and the prompt directive that
// now asks for correct orthography would have driven the frame decision's own
// evidence to zero on exactly the sessions it is meant to catch. An
// umlaut-bearing word cannot false-positive an English clause.
const NON_ENGLISH_LETTER = /[äöüßàâçéèêëîïôùûÿñõáíóúåøæ]/iu;
const NON_ENGLISH_WORD: ReadonlySet<string> = new Set([
  "ist", "sind", "ein", "eine", "einen", "einem", "einer", "eines",
  "der", "den", "dem", "das", "dass", "und", "oder", "ohne", "nicht",
  "wird", "werden", "kann", "koennen", "koennte", "muss", "muessen",
  "soll", "sollen", "damit", "mit", "fuer", "auf", "sich", "auch",
  "noch", "nur", "beim", "durch", "wenn", "weil", "ich", "wir", "ihr",
  "zu", "von", "im", "als", "aber", "schon", "immer", "kein", "keine",
  "sowie", "bzw", "bereits", "moechte", "moechten", "sollte", "waere",
  "für", "möchte", "möchten", "können", "könnte", "müssen", "wäre",
  // Derived from the customer corpus rather than invented: tokens that occur in
  // its German story clauses and in NONE of its English ones. Every entry is
  // held to the same bar as the list above — it must not be an ordinary English
  // word — which is why "die"/"man"/"hat"/"war" stay out while their German-only
  // siblings come in. Without these, a German card whose function words happen
  // to fall outside the original set scored under `MIN_DISTINCT_GERMAN` and
  // silently kept the English frame: live #1077 carried "nach", "statt", "sein"
  // and "sieht" and counted one marker.
  "nach", "statt", "des", "sein", "seine", "sieht", "sehen", "sofort",
  "diese", "dieser", "dieses", "dieselben", "alle", "allen", "vom", "zum",
  "zur", "sowohl", "wieder", "neue", "neuen", "ueber", "über",
]);

/**
 * Does this clause look like it is NOT in English?
 *
 * The guard on the two ENGLISH-shaped orthography rules below. Both are correct
 * English and both CORRUPT a German clause: German capitalizes its nouns, so
 * "Ziel ist ein …" lowercased to "ziel ist ein …" rewrites the user's own word,
 * and a German plural takes no indefinite article at all — which is how live
 * issue #3 came to be written as "As an Interne Mitarbeitende der Organisation".
 *
 * A clause reaches an English template carrying German content whenever the
 * project locale is `en` and the people are writing German — the locale is a
 * per-project `.loom.yml` property nobody has set. This is the backstop under
 * that: it cannot make the scaffolding German, only stop the scaffolding from
 * editing German words. `resolveContentLocale` is what picks the scaffolding,
 * and it reads this same evidence under two extra floors.
 *
 * Deliberately asymmetric. A false positive costs a capital letter that should
 * have been lowered — cosmetic. A false negative corrupts a word the grounding
 * gate greened byte-exact. The word list is closed and short for that reason:
 * every entry widens what it catches.
 */
export const looksNonEnglish = (value: string): boolean => {
  const s = value.normalize("NFC");
  if (NON_ENGLISH_LETTER.test(s)) return true;
  for (const w of s.toLowerCase().split(/[^\p{L}]+/u)) {
    if (w.length > 0 && NON_ENGLISH_WORD.has(w)) return true;
  }
  return false;
};

/**
 * How much of the sample has to carry foreign evidence before the WHOLE card is
 * judged foreign. One in eight words: German prose is far denser than that (its
 * function words alone clear it), and a lone accented proper noun in an English
 * sentence never does.
 */
const FOREIGN_EVIDENCE_RATIO = 1 / 8;

/**
 * The same judgement as `looksNonEnglish`, made over the WHOLE card instead of
 * one clause — and made by DENSITY, not by first hit.
 *
 * Per-clause is the wrong sample size for the decision it gates. A role is one
 * or two words: "Kundschaft" carries no umlaut and no function word, so
 * `looksNonEnglish` says English and the English template invented an article
 * for it — live #1078 was written "As a Kundschaft, I want Die Kundschaft findet
 * …", where the very next clause is unmistakably German. Judging the card,
 * every clause gets the benefit of the most legible one.
 *
 * WHY NOT `looksNonEnglish` OVER THE JOINED TEXT. That guard returns true on the
 * FIRST accented letter, which is the right asymmetry for one clause and the
 * wrong one for a whole card: the sample includes the acceptance criteria, so a
 * single Swiss proper noun in one criterion ("the Zürich office sees its own
 * rows") switched off every English repair for the story sentence — the invented
 * article, the sentence-case lead AND the infinitive marker — and wrote "As
 * Sales manager, I want Add an audit trail …". A bigger sample has to be read
 * proportionally or it is strictly worse than a small one.
 *
 * The asymmetry `looksNonEnglish` is built on is not lost by this: each clause
 * is still tested on its own inside `normalizeNarrativeClause` /
 * `spliceNarrativeClause`, so falling below the ratio only ever hands the
 * decision back to the per-clause guard. This signal can add protection to a
 * clause too short to judge; it can no longer take protection away.
 *
 * A project whose `.loom.yml` says `locale: de` gets the German template and
 * never reaches here. An UNCONFIGURED project can now also reach the German
 * template, via `resolveContentLocale` at the write sites — but this flag stays
 * the per-card guard on the ENGLISH orthography repairs, because the two
 * decisions are not the same one: this one runs on every render including the
 * judge artifact, samples the acceptance criteria, and only ever REMOVES an
 * edit. The frame decision samples the three story clauses alone and changes
 * which sentence gets written.
 */
export const narrativeContentIsForeign = (
  values: ReadonlyArray<string | undefined>,
): boolean => {
  const joined = values
    .filter((v) => v !== undefined)
    .join(" ")
    .normalize("NFC")
    .toLowerCase();
  let words = 0;
  let foreign = 0;
  for (const w of joined.split(/[^\p{L}]+/u)) {
    if (w.length === 0) continue;
    words += 1;
    if (NON_ENGLISH_LETTER.test(w) || NON_ENGLISH_WORD.has(w)) foreign += 1;
  }
  if (foreign === 0) return false;
  return foreign / words >= FOREIGN_EVIDENCE_RATIO;
};

const MIN_SAMPLE_WORDS = 12;
const MIN_DISTINCT_GERMAN = 3;

/**
 * Is this card's story written in a language that is not the default one?
 *
 * Tri-state ON PURPOSE. `"de"` means the sample carries positive German
 * evidence; `null` means NO VERDICT — the sample is English, or too short, or
 * accented-but-not-German. There is no `"en"` return, because nothing here
 * detects English: the floors below are German-evidence tests and their absence
 * is silence, not a finding. Collapsing the two used to be harmless (the caller
 * substituted the configured locale either way) right up until the write sites
 * started LOGGING the result, at which point a `locale: de` project applying a
 * three-word German card emitted `contentLocale: "en"` at warn — an operator
 * line asserting the opposite of the truth about the only thing it reports.
 *
 * The blind spot is the mirror: an English card on a `locale: de` project
 * returns `null`, indistinguishable from a short one, so that direction cannot
 * be logged. It needs a positive English detector, which does not exist here.
 *
 * THE THREE FLOORS, AND WHICH ONE ACTUALLY SEPARATES. Measured over every
 * framed story card on the two projects Loom writes to (69 cards, re-scanned
 * against this code): DENSITY is the separator and it is clean — the highest
 * non-switching card scores 0.106 and the lowest switching one 0.244, with the
 * 1/8 floor sitting in the empty band between them. `MIN_DISTINCT_GERMAN` is
 * NOT a discriminator and must not be described as one: live #1096 is an
 * English story naming German product strings, clears the floor at 4 distinct
 * words, and is excluded by density alone, while the lowest true positive
 * (#997) sits exactly ON the floor at 3. What the distinct floor buys is
 * LANGUAGE IDENTITY, which density cannot give: `NON_ENGLISH_LETTER` spans
 * French, Spanish, Nordic and Portuguese and `LOCALES` has a template for none
 * of them, so without it an accent-dense French card would be written
 * "Als … möchte ich". `MIN_SAMPLE_WORDS` keeps a two-word role from carrying
 * the decision; the shortest true positive runs 24 words.
 *
 * KNOWN MISSES, both English-framed German cards that stay English: #1077
 * (density 0.040 — the diacritics are transliterated AND the German words are
 * content words, not function words) and #1096 (German role, English means and
 * ends). Restoring #1077's umlauts does not rescue it: the folded letters are
 * not what put it under the floor. The asymmetry is deliberate — a miss leaves
 * today's behaviour, a false positive writes a German frame onto English prose.
 */
export const detectStoryLanguage = (
  storyClauses: ReadonlyArray<string | undefined>,
  /**
   * The language the session is HELD in — the interface language the user
   * reads Loom in (`AgentSession.replyLocale`). It can stand in for ONE thing
   * only: the language-identity floor (`MIN_DISTINCT_GERMAN`), which exists to
   * tell German from the other accented languages `LOCALES` has no template
   * for. A session held in German answers that question. It is never a vote
   * on whether the card is foreign at all — density still decides that, so an
   * English story typed in a German session keeps the English frame. At least
   * one German function word is still required.
   *
   * Live on dev, both from German sessions: #170 "As Angemeldete Nutzerinnen,
   * I want Einheit in der Bearbeitungsansicht änderbar, so that Menge der
   * Hauptposition passt sich an." (density 4/13, two distinct markers) and
   * #174 "As angemeldete Nutzerinnen, I want die Einheit … ändern, so that die
   * Mastermenge korrekt bleibt." (4/21, two markers).
   */
  sessionLanguage?: Locale,
): Locale | null => {
  const joined = storyClauses
    .filter((v) => v !== undefined)
    .join(" ")
    .normalize("NFC")
    .toLowerCase();
  let words = 0;
  const german = new Set<string>();
  for (const w of joined.split(/[^\p{L}]+/u)) {
    if (w.length === 0) continue;
    words += 1;
    if (NON_ENGLISH_WORD.has(w)) german.add(w);
  }
  if (words < MIN_SAMPLE_WORDS) return null;
  const identityFloor = sessionLanguage === "de" ? 1 : MIN_DISTINCT_GERMAN;
  if (german.size < identityFloor) return null;
  return narrativeContentIsForeign(storyClauses) ? "de" : null;
};

/**
 * The narrative FRAME's language, for one card.
 *
 * WHY THE FRAME IS NOT SIMPLY THE CONFIGURED LOCALE. `locale:` says which
 * language Loom AUTHORS in — the prompt directive, the questions, the UI copy.
 * The frame is not an authoring choice: it is three function words wrapped
 * around clause values the grounding gate already greened as the user's own
 * bytes. So at the default locale the frame follows those bytes. Live, it did
 * not: 10 of the 69 framed story cards on the two projects Loom writes to are
 * German, and every one was written "As a mitarbeitende der stadt zürich …, I want die ganze
 * firebase auth …, so that um bereit zu sein …" onto a customer's tracker,
 * permanently and with no signal.
 *
 * WHAT IT MAY READ, AND WHY THAT IS NOT NEGOTIABLE. The sample is the three
 * story clauses (role / means / ends) — the only values the frame touches —
 * and NEVER the acceptance criteria. That project's house style includes an
 * English story over a German acceptance-criteria block (live #1048, #1049,
 * #1050: role/means/ends carry zero German function words, the criteria are
 * wholly German), and a sample that includes the criteria fires on all three:
 * "Als an approved external user möchte ich the new project's unique ID …,
 * damit I always end up with a valid, working project ID." That is the mirror
 * of the defect this exists to fix, on the same tracker.
 *
 * An explicitly configured NON-DEFAULT locale is absolute: a `locale: de`
 * project keeps the German frame whatever the content says. With `LOCALES` at
 * two members that short-circuit is currently redundant by arithmetic — every
 * other path also returns `configured`, so no test can distinguish it — and it
 * is kept as the SEMANTIC statement, which is what a third locale would need.
 * `locale: en` is byte-identical to an absent key (`asLocale`), so it does NOT
 * pin the English frame; the remedy for a project that wants that is to plumb
 * the unset bit, not to loosen the floors.
 */
export const resolveContentLocale = (
  configured: Locale,
  storyClauses: ReadonlyArray<string | undefined>,
  /** See `detectStoryLanguage`'s `sessionLanguage`. */
  sessionLanguage?: Locale,
): Locale => {
  if (configured !== DEFAULT_LOCALE) return configured;
  return detectStoryLanguage(storyClauses, sessionLanguage) ?? configured;
};

const lowercaseSentenceCaseLead = (s: string): string => {
  const firstWord = /^\S+/.exec(s)?.[0] ?? "";
  // Sentence-case = uppercase then lowercase. Skip acronyms ("API"), the bare
  // pronoun "I" (no following lowercase), and internal-capital tokens ("GitLab").
  if (!/^[A-Z][a-z]/.test(firstWord)) return s;
  if (/[A-Z]/.test(firstWord.slice(1))) return s;
  // An English orthography rule applied to a German clause rewrites a noun the
  // grounding gate greened byte-exact.
  if (looksNonEnglish(s)) return s;
  return s.charAt(0).toLowerCase() + s.slice(1);
};

// Where a role phrase's HEAD noun stops: the first punctuation or function word
// that starts a modifier. English noun phrases are head-final, so the head is
// the last token BEFORE one of these — "Sales manager" heads on "manager",
// "Admins / superusers of the admin area" heads on "Admins", "finance managers
// who approve invoices" heads on "managers". Closed and short: every entry only
// makes the head EARLIER, and an over-long segment just means the last token is
// tested instead, which is the status quo answer.
const ROLE_HEAD_STOP: ReadonlySet<string> = new Set([
  "of", "for", "in", "on", "at", "with", "from", "to", "by", "about",
  "who", "that", "which", "whose", "and", "or",
  "the", "a", "an", "this", "these", "those", "my", "our", "their", "its",
  "der", "des", "von", "für", "fuer", "im", "mit", "aus", "die", "das", "und",
]);

// A present participle starts a post-modifier the head noun sits IN FRONT of:
// "user viewing the screening app overviews" heads on "user", not on
// "overviews". Cutting here is one-directional too — an "-ing" head ("Marketing
// team") just cuts to nothing, and no head means no plural, which is the article
// the phrase already had.
const PARTICIPLE = /^\p{L}{3,}ing$/u;

// Suffixes that make a trailing "s" part of the SINGULAR: "business", "status",
// "analysis", "gas", "chaos". Everything else ending in "s" is read as a plural.
const SINGULAR_S_ENDING = /(?:ss|us|is|as|os)$/iu;

/**
 * Does this role phrase's head noun read as a PLURAL?
 *
 * Used for one decision only: whether to put an indefinite article in front of
 * it. "As an Admins / superusers of the admin area" was written to live #1081
 * from Loom's own draft, and it is wrong in the template's own language.
 *
 * Deliberately crude and deliberately one-directional. A plural read wrong costs
 * a missing "a" ("As Sales manager") — odd but grammatical; a singular read
 * wrong costs the article the phrase needs. It is a spelling heuristic, not a
 * morphology engine: "As People" and "As Staff" are as far as it goes.
 */
const headNounIsPlural = (phrase: string): boolean => {
  const tokens = phrase
    .split(/[\s/(),–—-]+/u)
    .map((t) => t.replace(/[^\p{L}]/gu, ""))
    .filter((t) => t.length > 0);
  const head: string[] = [];
  for (const t of tokens) {
    if (ROLE_HEAD_STOP.has(t.toLowerCase()) || PARTICIPLE.test(t)) break;
    head.push(t);
  }
  // The split above cuts at "/" and "," too, so a segment that ran to the end
  // still heads on its own last token.
  const last = head[head.length - 1];
  if (last === undefined || last.length < 3) return false;
  return /s$/iu.test(last) && !SINGULAR_S_ENDING.test(last);
};

// Words a role phrase can OPEN on that already fill the article's place, so an
// indefinite article in front of them is always wrong: determiners, possessives,
// quantifiers and the indefinite pronouns ("everyone", "anyone"). Closed and
// short, and one-directional like the plural rule: every entry only ever removes
// a word Loom would have invented, and "As everyone …", "As the admin …", "As
// any signed-in user …" are grammatical without one.
const ROLE_DETERMINER_OPENERS: ReadonlySet<string> = new Set([
  "the", "this", "that", "these", "those",
  "my", "our", "your", "their", "his", "her", "its",
  "every", "each", "any", "all", "some", "no", "both", "either", "neither",
  "another", "one", "many", "most", "several",
  "everyone", "everybody", "anyone", "anybody", "someone", "somebody",
  "nobody",
]);

/** Does this role phrase open on a word that takes no indefinite article? */
const opensWithDeterminer = (phrase: string): boolean => {
  const first = (/^\S+/u.exec(phrase.trim())?.[0] ?? "")
    .replace(/[^\p{L}]/gu, "")
    .toLowerCase();
  return ROLE_DETERMINER_OPENERS.has(first);
};

const reconcileLeadingArticle = (s: string): string => {
  const m = /^(an?)\s+(\S.*)$/is.exec(s);
  if (!m) return s;
  const rest = m[2]!;
  const firstWord = /^\S+/.exec(rest)?.[0] ?? "";
  if (firstWord.length === 0) return s;
  // a/an agreement is English-only; a German plural takes no indefinite article
  // at all, so reconciling one there only picks a different wrong answer.
  if (looksNonEnglish(rest)) return s;
  // Neither does an English plural. The article here is the one the MODEL wrote
  // ("As an Admins / superusers …" arrives that way whole), and this rule
  // already owns that token — it rewrites a→an. Dropping it in front of a plural
  // is the same authority picking the third answer, which is "none".
  if (headNounIsPlural(rest)) return rest;
  // Same authority, same third answer, in front of a word that already fills
  // the article's place ("an everyone who …" → "everyone who …").
  if (opensWithDeterminer(rest)) return rest;
  return `${indefiniteArticle(firstWord)} ${rest}`;
};

/**
 * Normalize a narrative clause so it reads cleanly once the template prepends
 * its canonical connective. Pure; order is strip → lowercase → article. A no-op
 * on an already-clean clause (e.g. "a user", "list issues"), so the legacy EN
 * output is preserved byte-for-byte where the value was well-formed.
 */
export const normalizeNarrativeClause = (
  value: string,
  opts: NarrativeClauseOpts = {},
): string => {
  let s = value.trim();
  if (s.length === 0) return s;
  if (opts.connectives && opts.connectives.length > 0) {
    s = stripLeadingConnective(s, opts.connectives);
  }
  if (opts.lowercaseLead) s = lowercaseSentenceCaseLead(s);
  if (opts.article) s = reconcileLeadingArticle(s);
  return s;
};
